import { useEffect, useId, useState, type FocusEvent, type KeyboardEvent, type Ref } from "react";
import type { MunicipalityMatch } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import "../../design/components/text-field.css";
import { useT } from "../../i18n/locale";
import type { TabContext } from "../../session/tab-context";
import { municipalitiesQuery } from "../lead-queries";
import "./city-picker.css";

// Il selettore della città (piano M2 Task 17 per «Nuovo Lead», poi la scheda del Task 18): si scrive soltanto la città,
// anche una parte e senza accenti, e si sceglie fra al massimo otto comuni, cercati dal server (il catalogo ISTAT non
// entra nel browser); provincia, regione e nazione si compilano da sole (decisione del 25 settembre 2026). È una
// casella combinata ARIA: le frecce scorrono i comuni, Invio sceglie, Esc chiude l'elenco senza chiudere la finestra.
// Sulla scheda sta dentro la riga «Città», che ha già il suo nome: lì l'etichetta resta per gli screen reader e non si
// vede, e i tasti che l'elenco non usa (Invio senza comuni, Esc a elenco chiuso) arrivano alla riga.

/** Ciò che la casella contiene: il testo scritto e, se è stato scelto, il comune. */
export type CityValue = { text: string; match: MunicipalityMatch | null };

export const EMPTY_CITY: CityValue = { text: "", match: null };

/** Sotto i due caratteri il server non cerca. */
const MIN_SEARCH_LENGTH = 2;
const SEARCH_DELAY_MS = 150;

type Props = {
  id: string;
  tab: TabContext;
  value: CityValue;
  onChange: (value: CityValue) => void;
  error?: string;
  /** Arriva all'`<input>`, per riportarvi il focus dopo un errore. */
  ref?: Ref<HTMLInputElement>;
  /** L'etichetta resta per gli screen reader ma non si vede (la riga «Città» della scheda). */
  hideLabel?: boolean;
  /** I tasti che l'elenco non usa. */
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  /** Un'altra descrizione della casella, dopo la riga di provincia e regione (i tasti della riga sulla scheda). */
  describedBy?: string;
};

export function CityPicker({ id, tab, value, onChange, error, ref, hideLabel, onKeyDown, onBlur, describedBy }: Props) {
  const t = useT();
  const listId = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const typed = value.match ? "" : value.text.trim();
  const [query, setQuery] = useState(typed);

  // Si cerca 150 ms dopo l'ultimo tasto, e soltanto finché non si è scelto un comune.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(typed), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [typed]);

  const searching = query.length >= MIN_SEARCH_LENGTH && value.match === null;
  const results = useQuery({ ...municipalitiesQuery(tab, query), enabled: searching });
  const matches = searching ? (results.data ?? []) : [];
  const showList = open && typed.length >= MIN_SEARCH_LENGTH && value.match === null;
  const activeIndex = Math.min(active, Math.max(matches.length - 1, 0));
  const activeMatch = showList ? matches[activeIndex] : undefined;

  function pick(match: MunicipalityMatch) {
    onChange({ text: match.label, match });
    setOpen(false);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    // Mentre si cerca, Esc chiude l'elenco; frecce e Invio servono soltanto quando ci sono comuni da scegliere.
    let handled = false;
    if (showList) {
      handled = true;
      if (event.key === "Escape") setOpen(false);
      else if (event.key === "ArrowDown" && matches.length > 0) setActive((activeIndex + 1) % matches.length);
      else if (event.key === "ArrowUp" && matches.length > 0) setActive((activeIndex - 1 + matches.length) % matches.length);
      else if (event.key === "Enter" && activeMatch) pick(activeMatch);
      else handled = false;
    }
    if (handled) {
      // Invio non manda il modulo, Esc non chiude la finestra, le frecce non spostano il cursore.
      event.preventDefault();
      return;
    }
    onKeyDown?.(event);
  }

  const place = value.match
    ? t("cityPicker.place", { province: value.match.provinceName, region: value.match.regionName, country: t("cityPicker.italy") })
    : t("cityPicker.hint");
  let status: string | null = null;
  if (showList && (results.isFetching || query !== typed)) status = t("cityPicker.searching");
  else if (showList && matches.length === 0 && results.isSuccess) status = t("cityPicker.none", { q: typed });

  return (
    <div className="g-field g-city-picker">
      <label className={hideLabel ? "g-field__label g-visually-hidden" : "g-field__label"} htmlFor={id}>{t("cityPicker.label")}</label>
      <input
        ref={ref}
        id={id}
        className="g-field__input"
        type="text"
        role="combobox"
        autoComplete="off"
        maxLength={100}
        value={value.text}
        placeholder={t("cityPicker.placeholder")}
        aria-autocomplete="list"
        aria-expanded={showList && matches.length > 0}
        aria-controls={listId}
        aria-activedescendant={activeMatch ? `${listId}-${activeMatch.istatCode}` : undefined}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={[hintId, describedBy ?? null, error ? errorId : null].filter(Boolean).join(" ")}
        onChange={(event) => {
          onChange({ text: event.target.value, match: null });
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={(event) => {
          setOpen(false);
          onBlur?.(event);
        }}
        onKeyDown={handleKeyDown}
      />
      <ul id={listId} className="g-city-picker__list" role="listbox" aria-label={t("cityPicker.results")} hidden={!showList || matches.length === 0}>
        {matches.map((match, index) => (
          <li
            key={match.istatCode}
            id={`${listId}-${match.istatCode}`}
            role="option"
            tabIndex={-1}
            aria-selected={index === activeIndex}
            className="g-city-picker__option"
            // Il clic non deve togliere il focus alla casella prima di scegliere.
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => pick(match)}
            onKeyDown={(event) => {
              if (event.key === "Enter") pick(match);
            }}
          >
            <span className="g-city-picker__name">{match.label}</span>
            <span className="g-city-picker__sub">{t("cityPicker.place", { province: match.provinceName, region: match.regionName, country: t("cityPicker.italy") })}</span>
          </li>
        ))}
      </ul>
      {/* Una sola regione che si annuncia, sempre presente (una regione che compare già piena spesso non si legge): l'esito
          della ricerca. Vuota non si vede. La riga di provincia e regione descrive la casella e non si annuncia da sé. */}
      <p className={status ? "g-field__hint" : "g-visually-hidden"} role="status">
        {status}
      </p>
      <p id={hintId} className="g-field__hint">
        {place}
      </p>
      {error ? (
        <p id={errorId} className="g-field__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
